import React, { useState, useEffect } from "react";
import api from "../api/axios";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaFileInvoice, FaCheck, FaTimes, FaPlus, FaTrash } from "react-icons/fa";
import { useTranslation } from "../context/LanguageContext";
import "../styles/common.css";
import "./AjouterFacture.css";

// Helper function to generate unique IDs for achats
const generateAchatId = () => {
  return `achat_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
};

export default function AjouterFacture() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [produits, setProduits] = useState([]);

  const [formData, setFormData] = useState({
    numero: "",
    fournisseur: "",
    date_achat: "",
    notes: "",
    facture_image: null,
  });

  const [achats, setAchats] = useState([
    {
      id: generateAchatId(),
      produit: "",
      quantite: "",
      prix_unitaire: "",
      date_expiration: "",
      numero_lot: "",
    },
  ]);

  const [fieldErrors, setFieldErrors] = useState({
    numero: "",
    fournisseur: "",
    date_achat: "",
  });

  const [touchedFields, setTouchedFields] = useState({
    numero: false,
    fournisseur: false,
    date_achat: false,
  });

  const [achatsErrors, setAchatsErrors] = useState([{}]);

  // Migration step: ensure all achats have stable IDs
  useEffect(() => {
    setAchats(prevAchats => {
      const needsMigration = prevAchats.some(achat => !achat.id);
      if (needsMigration) {
        return prevAchats.map(achat => 
          achat.id ? achat : { ...achat, id: generateAchatId() }
        );
      }
      return prevAchats;
    });
  }, []);

  useEffect(() => {
    const fetchProduits = async () => {
      try {
        const response = await api.get("/api/produits-pharmaceutiques/?actif=true");
        setProduits(response.data);
      } catch (err) {
        console.error("Erreur lors du chargement des produits:", err);
      }
    };
    fetchProduits();
  }, []);

  const validateRequiredField = (name, value) => {
    if (!value || (typeof value === 'string' && value.trim() === "") || value === null) {
      return "Ce champ est obligatoire";
    }
    return "";
  };

  const validateField = (name, value) => {
    if (name === "numero" || name === "fournisseur" || name === "date_achat") {
      return validateRequiredField(name, value);
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    if ((name === "numero" || name === "fournisseur" || name === "date_achat") && (touchedFields[name] || fieldErrors[name])) {
      const error = validateField(name, value);
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        facture_image: file,
      });
    }
  };

  const handleAchatChange = (index, field, value) => {
    const newAchats = [...achats];
    newAchats[index][field] = value;
    setAchats(newAchats);
  };

  const addAchat = () => {
    setAchats([
      ...achats,
      {
        id: generateAchatId(),
        produit: "",
        quantite: "",
        prix_unitaire: "",
        date_expiration: "",
        numero_lot: "",
      },
    ]);
    setAchatsErrors([...achatsErrors, {}]);
  };

  const removeAchat = (index) => {
    if (achats.length > 1) {
      const newAchats = achats.filter((_, i) => i !== index);
      const newErrors = achatsErrors.filter((_, i) => i !== index);
      setAchats(newAchats);
      setAchatsErrors(newErrors);
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (touchedFields.hasOwnProperty(name)) {
      setTouchedFields(prev => ({
        ...prev,
        [name]: true
      }));
    }

    if (name === "numero" || name === "fournisseur" || name === "date_achat") {
      const error = validateField(name, value);
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const calculateTotalAmount = () => {
    return achats.reduce((sum, achat) => {
      const quantite = parseFloat(achat.quantite) || 0;
      const prixUnitaire = parseFloat(achat.prix_unitaire) || 0;
      return sum + (quantite * prixUnitaire);
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newTouchedFields = {
      numero: true,
      fournisseur: true,
      date_achat: true,
    };
    setTouchedFields(newTouchedFields);

    const newFieldErrors = {
      numero: validateField("numero", formData.numero),
      fournisseur: validateField("fournisseur", formData.fournisseur),
      date_achat: validateField("date_achat", formData.date_achat),
    };
    setFieldErrors(newFieldErrors);

    // Vérifier qu'au moins un produit est valide
    const totalCalculated = calculateTotalAmount();
    if (totalCalculated <= 0) {
      toast.error("Veuillez ajouter au moins un produit avec une quantité et un prix valides.");
      return;
    }

    // Valider les achats
    const newAchatsErrors = achats.map((achat, index) => {
      const errors = {};
      if (!achat.produit) errors.produit = "Produit obligatoire";
      if (!achat.quantite || parseFloat(achat.quantite) <= 0) errors.quantite = "Quantité invalide";
      if (!achat.prix_unitaire || parseFloat(achat.prix_unitaire) <= 0) errors.prix_unitaire = "Prix unitaire invalide";
      return errors;
    });
    setAchatsErrors(newAchatsErrors);

    const hasErrors = Object.values(newFieldErrors).some(error => error !== "") ||
      newAchatsErrors.some(err => Object.keys(err).length > 0);

    if (hasErrors) {
      toast.error("Veuillez corriger les erreurs avant de continuer.");
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    setLoading(true);
    let factureId = null;
    const createdAchatIds = [];
    let imageUploaded = false;

    try {
      // Calculer le montant total automatiquement
      const montantTotal = calculateTotalAmount();

      // 1. Créer la facture
      const factureData = {
        numero: formData.numero,
        fournisseur: formData.fournisseur,
        date_achat: formData.date_achat,
        montant_total: montantTotal,
        notes: formData.notes || null,
      };

      const factureResponse = await api.post("/api/factures/", factureData);
      factureId = factureResponse.data.id;

      // 2. Ajouter les produits (achats) avec rollback en cas d'erreur
      try {
        for (let i = 0; i < achats.length; i++) {
          const achat = achats[i];
          const achatData = {
            facture: factureId,
            produit: parseInt(achat.produit),
            quantite: parseFloat(achat.quantite),
            prix_unitaire: parseFloat(achat.prix_unitaire),
            date_expiration: achat.date_expiration || null,
            numero_lot: achat.numero_lot || null,
          };
          
          const achatResponse = await api.post("/api/achats-produits/", achatData);
          createdAchatIds.push(achatResponse.data.id);
        }
      } catch (achatError) {
        // Erreur lors de la création d'un achat - rollback
        console.error("Erreur lors de la création d'un achat:", achatError);
        
        // Supprimer tous les achats créés
        const rollbackErrors = [];
        for (const achatId of createdAchatIds) {
          try {
            await api.delete(`/api/achats-produits/${achatId}/`);
          } catch (deleteError) {
            rollbackErrors.push(`Échec de suppression de l'achat ${achatId}: ${deleteError.message}`);
            console.error(`Erreur lors de la suppression de l'achat ${achatId}:`, deleteError);
          }
        }

        // Supprimer la facture
        try {
          await api.delete(`/api/factures/${factureId}/`);
        } catch (deleteError) {
          rollbackErrors.push(`Échec de suppression de la facture: ${deleteError.message}`);
          console.error("Erreur lors de la suppression de la facture:", deleteError);
        }

        // Construire le message d'erreur détaillé
        const succeededCount = createdAchatIds.length;
        const totalCount = achats.length;
        let errorMessage = `Erreur lors de la création des produits. `;
        
        if (succeededCount > 0) {
          errorMessage += `${succeededCount} produit(s) créé(s) sur ${totalCount} avant l'erreur. `;
        }
        
        errorMessage += `Tous les éléments ont été annulés (rollback). `;
        errorMessage += `Erreur: ${achatError.response?.data?.detail || 
          achatError.response?.data?.message || 
          Object.values(achatError.response?.data || {}).flat().join(", ") || 
          achatError.message || 
          "Erreur inconnue"}`;

        if (rollbackErrors.length > 0) {
          errorMessage += ` Avertissement: certaines suppressions ont échoué: ${rollbackErrors.join("; ")}`;
        }

        toast.error(errorMessage);
        setShowConfirmModal(false);
        return;
      }

      // 3. Uploader l'image si présente
      if (formData.facture_image) {
        try {
          const formDataImage = new FormData();
          formDataImage.append('facture_image', formData.facture_image);
          await api.patch(`/api/factures/${factureId}/`, formDataImage, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          imageUploaded = true;
        } catch (imageError) {
          // Erreur lors de l'upload de l'image - rollback complet
          console.error("Erreur lors de l'upload de l'image:", imageError);
          
          // Supprimer tous les achats créés
          const rollbackErrors = [];
          for (const achatId of createdAchatIds) {
            try {
              await api.delete(`/api/achats-produits/${achatId}/`);
            } catch (deleteError) {
              rollbackErrors.push(`Échec de suppression de l'achat ${achatId}: ${deleteError.message}`);
              console.error(`Erreur lors de la suppression de l'achat ${achatId}:`, deleteError);
            }
          }

          // Supprimer la facture
          try {
            await api.delete(`/api/factures/${factureId}/`);
          } catch (deleteError) {
            rollbackErrors.push(`Échec de suppression de la facture: ${deleteError.message}`);
            console.error("Erreur lors de la suppression de la facture:", deleteError);
          }

          let errorMessage = `Erreur lors de l'upload de l'image. `;
          errorMessage += `Tous les éléments ont été annulés (rollback). `;
          errorMessage += `Erreur: ${imageError.response?.data?.detail || 
            imageError.response?.data?.message || 
            Object.values(imageError.response?.data || {}).flat().join(", ") || 
            imageError.message || 
            "Erreur inconnue"}`;

          if (rollbackErrors.length > 0) {
            errorMessage += ` Avertissement: certaines suppressions ont échoué: ${rollbackErrors.join("; ")}`;
          }

          toast.error(errorMessage);
          setShowConfirmModal(false);
          return;
        }
      }

      // Succès complet
      setShowConfirmModal(false);
      toast.success("Facture ajoutée avec succès !");
      setTimeout(() => {
        navigate("/factures");
      }, 500);
    } catch (err) {
      // Erreur lors de la création de la facture elle-même
      console.error("Erreur lors de la création de la facture:", err);
      
      // Si la facture a été créée mais qu'on n'a pas encore son ID, essayer de la supprimer
      // (peu probable mais possible en cas d'erreur réseau)
      if (factureId) {
        try {
          await api.delete(`/api/factures/${factureId}/`);
        } catch (deleteError) {
          console.error("Erreur lors de la suppression de la facture après échec:", deleteError);
        }
      }

      const errorMessage = err.response?.data?.detail ||
        err.response?.data?.message ||
        Object.values(err.response?.data || {}).flat().join(", ") ||
        "Erreur lors de l'ajout de la facture !";
      toast.error(errorMessage);
      setShowConfirmModal(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
  };

  const isFormValid = () => {
    if (!formData.numero || !formData.numero.trim()) return false;
    if (!formData.fournisseur || !formData.fournisseur.trim()) return false;
    if (!formData.date_achat) return false;
    const totalCalculated = calculateTotalAmount();
    if (totalCalculated <= 0) return false;
    if (achats.some(a => !a.produit || !a.quantite || !a.prix_unitaire)) return false;
    return true;
  };

  const getProduitNom = (produitId) => {
    const produit = produits.find(p => p.id === parseInt(produitId));
    return produit ? produit.nom : "";
  };

  return (
    <div className="ajouter-facture-page">
      <div className="title-badge">
        <FaFileInvoice className="page-title-icon" aria-hidden="true" />
        <span>AJOUTER UNE FACTURE</span>
      </div>

      <form onSubmit={handleSubmit} className="ajouter-facture-form">
        <div className="form-info-banner">
          <span className="info-icon">ℹ️</span>
          <div className="info-content">
            <strong>Champs obligatoires :</strong>
            <span className="required-indicator">*</span> Les champs marqués d'un astérisque rouge sont obligatoires.
          </div>
        </div>

        <div className="form-section-container">
          <div className="form">
            <label htmlFor="numero">
              {t.invoiceNumberLabel} <span className="required-indicator">*</span>
            </label>
            <input
              type="text"
              id="numero"
              name="numero"
              placeholder="Ex: FAC-2025-001"
              value={formData.numero}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.numero && touchedFields.numero ? "input-error" : ""}
              maxLength={100}
            />
            {fieldErrors.numero && touchedFields.numero && (
              <span className="field-error-message">{fieldErrors.numero}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="fournisseur">
              {t.supplier} <span className="required-indicator">*</span>
            </label>
            <input
              type="text"
              id="fournisseur"
              name="fournisseur"
              placeholder={t.supplierName}
              value={formData.fournisseur}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.fournisseur && touchedFields.fournisseur ? "input-error" : ""}
              maxLength={255}
            />
            {fieldErrors.fournisseur && touchedFields.fournisseur && (
              <span className="field-error-message">{fieldErrors.fournisseur}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="date_achat">
              {t.purchaseDateLabel} <span className="required-indicator">*</span>
            </label>
            <input
              type="date"
              id="date_achat"
              name="date_achat"
              value={formData.date_achat}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.date_achat && touchedFields.date_achat ? "input-error" : ""}
            />
            {fieldErrors.date_achat && touchedFields.date_achat && (
              <span className="field-error-message">{fieldErrors.date_achat}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="montant_total">
              {t.totalAmountLabel} (FCFA) <span className="required-indicator">*</span>
              <span className="auto-calculated-badge">({t.autoCalculated})</span>
            </label>
            <input
              type="text"
              id="montant_total"
              name="montant_total"
              value={calculateTotalAmount().toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " FCFA"}
              disabled
              readOnly
              className="input-disabled"
            />
            <p className="calculated-total">
              Montant calculé à partir des produits achetés
            </p>
          </div>

          <div className="form full-width">
            <label htmlFor="notes">{t.notes}</label>
            <textarea
              id="notes"
              name="notes"
              placeholder={t.additionalNotes}
              value={formData.notes}
              onChange={handleChange}
              rows="3"
            />
          </div>

          <div className="form full-width">
            <label htmlFor="facture_image">{t.invoiceImage}</label>
            <input
              type="file"
              id="facture_image"
              name="facture_image"
              accept="image/*"
              onChange={handleFileChange}
            />
            {formData.facture_image && (
              <p className="file-name">{formData.facture_image.name}</p>
            )}
          </div>
        </div>

        {/* Section des produits achetés */}
        <div className="produits-section">
          <div className="section-header">
            <h3>Produits achetés</h3>
            <button type="button" className="add-produit-button" onClick={addAchat}>
              <FaPlus />
              Ajouter un produit
            </button>
          </div>

          {achats.map((achat, index) => (
            <div key={achat.id} className="achat-row">
              <div className="achat-header">
                <h4>Produit {index + 1}</h4>
                {achats.length > 1 && (
                  <button
                    type="button"
                    className="remove-achat-button"
                    onClick={() => removeAchat(index)}
                  >
                    <FaTrash />
                  </button>
                )}
              </div>

              <div className="form-section-container">
                <div className="form">
                  <label>
                    {t.product} <span className="required-indicator">*</span>
                  </label>
                  <select
                    value={achat.produit}
                    onChange={(e) => handleAchatChange(index, "produit", e.target.value)}
                    className={achatsErrors[index]?.produit ? "input-error" : ""}
                  >
                    <option value="">{t.selectProduct}</option>
                    {produits.map((produit) => (
                      <option key={produit.id} value={produit.id}>
                        {produit.nom} ({produit.type_produit})
                      </option>
                    ))}
                  </select>
                  {achatsErrors[index]?.produit && (
                    <span className="field-error-message">{achatsErrors[index].produit}</span>
                  )}
                </div>

                <div className="form">
                  <label>
                    {t.quantityLabel} <span className="required-indicator">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={achat.quantite}
                    onChange={(e) => handleAchatChange(index, "quantite", e.target.value)}
                    className={achatsErrors[index]?.quantite ? "input-error" : ""}
                  />
                  {achatsErrors[index]?.quantite && (
                    <span className="field-error-message">{achatsErrors[index].quantite}</span>
                  )}
                </div>

                <div className="form">
                  <label>
                    {t.unitPrice} <span className="required-indicator">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={achat.prix_unitaire}
                    onChange={(e) => handleAchatChange(index, "prix_unitaire", e.target.value)}
                    className={achatsErrors[index]?.prix_unitaire ? "input-error" : ""}
                  />
                  {achatsErrors[index]?.prix_unitaire && (
                    <span className="field-error-message">{achatsErrors[index].prix_unitaire}</span>
                  )}
                  {achat.quantite && achat.prix_unitaire && (
                    <p className="prix-total">
                      Total: {(parseFloat(achat.quantite) * parseFloat(achat.prix_unitaire)).toLocaleString('fr-FR')} FCFA
                    </p>
                  )}
                </div>

                <div className="form">
                  <label>{t.expirationDate}</label>
                  <input
                    type="date"
                    value={achat.date_expiration}
                    onChange={(e) => handleAchatChange(index, "date_expiration", e.target.value)}
                  />
                </div>

                <div className="form">
                  <label>{t.lotNumber}</label>
                  <input
                    type="text"
                    value={achat.numero_lot}
                    onChange={(e) => handleAchatChange(index, "numero_lot", e.target.value)}
                    placeholder={t.lotNumberPlaceholder}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="actions">
          <button
            type="submit"
            className="save-but"
            disabled={!isFormValid() || loading}
          >
            <FaCheck />
            <span>{loading ? "Enregistrement..." : "Enregistrer"}</span>
          </button>
          <button
            type="button"
            className="cancel-but"
            onClick={() => navigate("/factures")}
            disabled={loading}
          >
            <FaTimes />
            <span>Annuler</span>
          </button>
        </div>
      </form>

      {/* Modal de confirmation */}
      {showConfirmModal && (
        <div className="confirm-modal-overlay" onClick={handleCancelConfirm}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-header">
              <FaCheck className="confirm-modal-icon" />
              <h3>Confirmer l'ajout de la facture</h3>
            </div>
            <div className="confirm-modal-body">
              <p className="confirm-intro">
                Veuillez vérifier les informations avant de confirmer l'ajout :
              </p>
              <div className="confirm-summary">
                <div className="summary-row">
                  <span className="summary-label">Numéro :</span>
                  <span className="summary-value">{formData.numero}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">{t.supplier}:</span>
                  <span className="summary-value">{formData.fournisseur}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">{t.purchaseDate}:</span>
                  <span className="summary-value">{new Date(formData.date_achat).toLocaleDateString('fr-FR')}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">{t.totalAmountLabel}:</span>
                  <span className="summary-value">{calculateTotalAmount().toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} FCFA</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">{t.numberOfProducts}:</span>
                  <span className="summary-value">{achats.length}</span>
                </div>
              </div>
            </div>
            <div className="confirm-modal-footer">
              <button
                type="button"
                onClick={handleCancelConfirm}
                className="confirm-modal-cancel"
                disabled={loading}
              >
                <FaTimes />
                <span>Annuler</span>
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                className="confirm-modal-confirm"
                disabled={loading}
              >
                <FaCheck />
                <span>{loading ? "Enregistrement..." : "Confirmer"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}